// src/pages/student-portal/payment-methods/PaymentMethodsPage.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Button,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Alert,
    Snackbar,
    Tooltip,
    Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const PURPLE_ACCENT = '#8b5cf6';

const CARD_BRAND_COLORS = {
    visa: '#1a1f71',
    mastercard: '#eb001b',
    amex: '#2e77bb',
    discover: '#ff6000',
    default: '#64748b',
};

const getCardIcon = (brand) => {
    switch (brand?.toLowerCase()) {
        case 'visa':
        case 'mastercard':
        case 'amex':
        case 'discover':
            return <CreditCardIcon sx={{ fontSize: 32, color: CARD_BRAND_COLORS[brand.toLowerCase()] || CARD_BRAND_COLORS.default }} />;
        case 'bank':
            return <AccountBalanceIcon sx={{ fontSize: 32, color: '#64748b' }} />;
        default:
            return <PaymentIcon sx={{ fontSize: 32, color: '#64748b' }} />;
    }
};

const INITIAL_FORM = {
    card_number: '',
    card_holder: '',
    expiry_month: '',
    expiry_year: '',
    cvv: '',
};

export default function StPaymentMethodsPage() {
    const [methods, setMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchPaymentMethods = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get('/payment-methods');
            const data = response.data;
            setMethods(data.data || data.payment_methods || data.paymentMethods || []);
        } catch (err) {
            console.error('Failed to fetch payment methods:', err);
            setError(err.response?.data?.message || 'Failed to load payment methods.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = 'Payment Methods | Student Portal';
        fetchPaymentMethods();
    }, [fetchPaymentMethods]);

    const handleSetDefault = async (method) => {
        const methodId = method.id || method._id;
        try {
            await axiosInstance.post(`/payment-methods/${methodId}/default`);
            setSnackbar({ open: true, message: 'Default payment method updated!', severity: 'success' });
            fetchPaymentMethods();
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Failed to set default payment method.',
                severity: 'error',
            });
        }
    };

    const handleDelete = async (method) => {
        const methodId = method.id || method._id;
        if (!window.confirm('Are you sure you want to remove this payment method?')) return;
        try {
            await axiosInstance.delete(`/payment-methods/${methodId}`);
            setSnackbar({ open: true, message: 'Payment method removed!', severity: 'success' });
            fetchPaymentMethods();
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Failed to remove payment method.',
                severity: 'error',
            });
        }
    };

    const handleSubmit = async () => {
        if (!formData.card_number.trim() || !formData.card_holder.trim()) {
            setSnackbar({ open: true, message: 'Card number and holder name are required.', severity: 'warning' });
            return;
        }
        setSubmitting(true);
        try {
            await axiosInstance.post('/payment-methods', formData);
            setSnackbar({ open: true, message: 'Payment method added!', severity: 'success' });
            setDialogOpen(false);
            setFormData(INITIAL_FORM);
            fetchPaymentMethods();
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Failed to add payment method.',
                severity: 'error',
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                        Payment Methods
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mt: 0.5 }}>
                        Manage your saved payment methods for course purchases
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchPaymentMethods} sx={{ color: '#64748b' }}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        onClick={() => setDialogOpen(true)}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            bgcolor: PURPLE_ACCENT,
                            borderRadius: '10px',
                            textTransform: 'none',
                            px: 3,
                            '&:hover': { bgcolor: '#7c3aed' },
                        }}
                    >
                        Add Payment Method
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={32} sx={{ color: PURPLE_ACCENT }} />
                </Box>
            ) : methods.length === 0 ? (
                <Paper
                    sx={{
                        p: 6,
                        textAlign: 'center',
                        borderRadius: '16px',
                        border: '1px solid',
                        borderColor: 'grey.100',
                    }}
                >
                    <CreditCardIcon sx={{ fontSize: 64, color: '#e2e8f0', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1 }}>
                        No Payment Methods
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mb: 3 }}>
                        Add a payment method to purchase courses
                    </Typography>
                    <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        onClick={() => setDialogOpen(true)}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            bgcolor: PURPLE_ACCENT,
                            borderRadius: '10px',
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#7c3aed' },
                        }}
                    >
                        Add Payment Method
                    </Button>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {methods.map((method) => {
                        const methodId = method.id || method._id;
                        const isDefault = method.is_default || method.isDefault || false;
                        const brand = method.brand || method.card_brand || method.type || 'card';
                        const last4 = method.last4 || method.last_four || '****';
                        const expiry = method.expiry || method.exp_month
                            ? `${String(method.exp_month || '').padStart(2, '0')}/${method.exp_year || ''}`
                            : method.expiry_date || '--';

                        return (
                            <Grid item xs={12} sm={6} md={4} key={methodId}>
                                <Card
                                    sx={{
                                        borderRadius: '16px',
                                        border: isDefault ? `2px solid ${PURPLE_ACCENT}` : '1px solid',
                                        borderColor: isDefault ? PURPLE_ACCENT : 'grey.100',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
                                        },
                                        position: 'relative',
                                    }}
                                >
                                    {isDefault && (
                                        <Chip
                                            label="Default"
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: 12,
                                                right: 12,
                                                fontFamily,
                                                fontWeight: 600,
                                                fontSize: '0.7rem',
                                                bgcolor: `${PURPLE_ACCENT}15`,
                                                color: PURPLE_ACCENT,
                                            }}
                                        />
                                    )}
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ mb: 2 }}>
                                            {getCardIcon(brand)}
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontFamily,
                                                fontWeight: 700,
                                                fontSize: '1.2rem',
                                                color: '#1e293b',
                                                letterSpacing: '2px',
                                                mb: 1,
                                            }}
                                        >
                                            **** **** **** {last4}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Box>
                                                <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                                                    Card Holder
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                                    {method.card_holder || method.cardHolder || method.holder_name || method.name || '--'}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                                                    Expires
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                                    {expiry}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Divider sx={{ mb: 1.5 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Chip
                                                label={brand.charAt(0).toUpperCase() + brand.slice(1)}
                                                size="small"
                                                sx={{
                                                    fontFamily,
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                    bgcolor: '#f1f5f9',
                                                    color: '#64748b',
                                                }}
                                            />
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {!isDefault && (
                                                    <Tooltip title="Set as default">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleSetDefault(method)}
                                                            sx={{ color: '#f59e0b' }}
                                                        >
                                                            <StarOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Remove">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDelete(method)}
                                                        sx={{ color: '#ef4444' }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Add Payment Method Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    Add Payment Method
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                        <TextField
                            label="Card Number"
                            fullWidth
                            placeholder="1234 5678 9012 3456"
                            value={formData.card_number}
                            onChange={(e) => setFormData((prev) => ({ ...prev, card_number: e.target.value }))}
                            inputProps={{ maxLength: 19 }}
                            sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                        />
                        <TextField
                            label="Card Holder Name"
                            fullWidth
                            value={formData.card_holder}
                            onChange={(e) => setFormData((prev) => ({ ...prev, card_holder: e.target.value }))}
                            sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                        />
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <TextField
                                    label="Month"
                                    fullWidth
                                    placeholder="MM"
                                    value={formData.expiry_month}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, expiry_month: e.target.value }))}
                                    inputProps={{ maxLength: 2 }}
                                    sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    label="Year"
                                    fullWidth
                                    placeholder="YY"
                                    value={formData.expiry_year}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, expiry_year: e.target.value }))}
                                    inputProps={{ maxLength: 4 }}
                                    sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    label="CVV"
                                    fullWidth
                                    placeholder="123"
                                    type="password"
                                    value={formData.cvv}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, cvv: e.target.value }))}
                                    inputProps={{ maxLength: 4 }}
                                    sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={() => { setDialogOpen(false); setFormData(INITIAL_FORM); }}
                        sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            bgcolor: PURPLE_ACCENT,
                            borderRadius: '10px',
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#7c3aed' },
                        }}
                    >
                        Add Card
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ fontFamily, borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
